import { isShortcutsDisabledAtom } from '@/app-shell/layout/ShortcutsHelper/shortcutsAtom';
import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { hotkeysList } from '@/lists/hotkeys';
import { ShortcutContext, trackShortcutUsed } from '@heidi/analytics/events';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash-es';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { type RefObject } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';
import { type VListHandle } from 'virtua';

type useSessionsListHotkeysProps = {
  allSessionIds: string[];
  vListRef: RefObject<VListHandle>;
  handleDeleteSelected: () => void;
  selectedSessionIds: string[];
};

export const useSessionsListHotkeys = ({
  allSessionIds,
  vListRef,
  handleDeleteSelected,
  selectedSessionIds,
}: useSessionsListHotkeysProps) => {
  const router = useRouter();
  const isShortcutsDisabled = useAtomValue(isShortcutsDisabledAtom);
  const searchParams = useSearchParams();
  const currentSessionId = searchParams.get('id') ?? router.query.id?.[0];
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const isSidebarSessionListOpen = sidebarOpenTab === SidebarTab.SESSION_LIST;
  useHotkeys(
    hotkeysList.changeSession.hotkeys.up,
    () => {
      if (isEmpty(router.query.id) || allSessionIds.length < 2 || !currentSessionId)
        return;
      const currentIndex = allSessionIds.indexOf(currentSessionId);

      if (currentIndex > 0) {
        const previousSessionId = allSessionIds[currentIndex - 1]!;
        void (async () => {
          await router.push(`/scribe/session/${previousSessionId}`);
          vListRef.current?.scrollToIndex(currentIndex - 1, {
            align: 'start',
            smooth: true,
          });
        })();
      }
      trackShortcutUsed({
        shortcutName: ShortcutContext.CHANGE_SESSION,
      });
    },
    {
      scopes: hotkeysList.changeSession.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled,
    },
    [currentSessionId, allSessionIds, router, vListRef, isShortcutsDisabled]
  );

  useHotkeys(
    hotkeysList.changeSession.hotkeys.down,
    () => {
      if (isEmpty(router.query.id) || allSessionIds.length < 2 || !currentSessionId)
        return;
      const currentIndex = allSessionIds.indexOf(currentSessionId);

      if (currentIndex < allSessionIds.length - 1) {
        const nextSessionId = allSessionIds[currentIndex + 1]!;
        void (async () => {
          await router.push(`/scribe/session/${nextSessionId}`);
          vListRef.current?.scrollToIndex(currentIndex + 1, {
            align: 'start',
            smooth: true,
          });
        })();
      }
      trackShortcutUsed({
        shortcutName: ShortcutContext.CHANGE_SESSION,
      });
    },
    {
      scopes: hotkeysList.changeSession.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled,
    },
    [router, allSessionIds, currentSessionId, vListRef, isShortcutsDisabled]
  );

  useHotkeys(
    Key.ArrowDown,
    () => {
      const currentFocusedElement = document.activeElement as HTMLAnchorElement;
      const currentFocusedSessionId = currentFocusedElement.id;

      if (currentFocusedSessionId && allSessionIds.includes(currentFocusedSessionId)) {
        const currentIndex = allSessionIds.indexOf(currentFocusedSessionId);

        let targetSessionId: string | undefined;

        if (currentIndex < allSessionIds.length - 1) {
          // If not the last item, go to the next one
          targetSessionId = allSessionIds[currentIndex + 1];
        } else {
          // If it's the last item, wrap around to the first session
          targetSessionId = allSessionIds[0];
        }

        if (!targetSessionId) return;
        const targetElement = document.getElementById(targetSessionId);
        if (targetElement) {
          targetElement.focus();
        }
      }
    },
    {
      enabled: isSidebarSessionListOpen,
      preventDefault: true,
    },
    [allSessionIds, isSidebarSessionListOpen]
  );

  useHotkeys(
    Key.ArrowUp,
    () => {
      const currentFocusedElement = document.activeElement as HTMLAnchorElement;
      const currentFocusedSessionId = currentFocusedElement.id;

      if (currentFocusedSessionId && allSessionIds.includes(currentFocusedSessionId)) {
        const currentIndex = allSessionIds.indexOf(currentFocusedSessionId);

        let targetSessionId: string | undefined;

        if (currentIndex > 0) {
          // If not the first item, go to the previous one
          targetSessionId = allSessionIds[currentIndex - 1];
        } else {
          // If it's the first item, wrap around to the last session
          targetSessionId = allSessionIds[allSessionIds.length - 1];
        }

        if (!targetSessionId) return;
        const targetElement = document.getElementById(targetSessionId);
        if (targetElement) {
          targetElement.focus();
        }
      }
    },
    {
      enabled: isSidebarSessionListOpen,
      preventDefault: true,
    },
    [allSessionIds, isSidebarSessionListOpen]
  );

  useHotkeys(
    hotkeysList.deleteSelectedSessions.hotkeys,
    () => {
      handleDeleteSelected();

      trackShortcutUsed({
        shortcutName: ShortcutContext.DELETE_SELECTED_SESSIONS,
      });
    },
    {
      preventDefault: true,
      scopes: hotkeysList.deleteSelectedSessions.scopes,
      enabled: selectedSessionIds !== null && selectedSessionIds.length > 0,
    },
    [handleDeleteSelected, selectedSessionIds]
  );
};
